import { selector, selectorFamily } from 'recoil';
import { CommunicationTypeEnum } from '@zurich/api/party';
import { allValid, validateSchema } from '@zurich/common/recoil';
import { partyAreaCodeSchema, partyCitySchema, partyCommunicationRoleSchema, partyCountryCallingCodeSchema, partyCountrySchema, partyDoorNumberSchema, partyEmailSchema, partyForeignPostalCodeSchema, partyHouseNumberFromSchema, partyHouseNumberToSchema, partyLocalPhoneNumberSchema, partyPoboxNumberSchema, partyPostalCodeSchema, partyStaircaseSchema, partyStreetSchema, } from '../../forms';
import { localPartyCommunicationChannelAreaCodeSelector, localPartyCommunicationChannelCitySelectorFamily, localPartyCommunicationChannelCommunicationPhoneNumberSelector, localPartyCommunicationChannelCommunicationRoleSelectorFamily, localPartyCommunicationChannelCountryCallingCodeSelector, localPartyCommunicationChannelCountrySelectorFamily, localPartyCommunicationChannelDoorNumberSelector, localPartyCommunicationChannelEmailAddressSelector, localPartyCommunicationChannelHouseNumberFromSelector, localPartyCommunicationChannelHouseNumberToSelector, localPartyCommunicationChannelPoboxNumberSelector, localPartyCommunicationChannelPostalCodeSelectorFamily, localPartyCommunicationChannelStaircaseSelector, localPartyCommunicationChannelStreetSelector, } from '../selectors/localPartyCommunicationChannel';
export const localPartyCommunicationChannelCommunicationRoleValidationSelectorFamily = selectorFamily({
    key: 'validation/local/partyCommunicationChannel/communicationRole',
    get: (communicationType) => ({ get }) => {
        const communicationRole = get(localPartyCommunicationChannelCommunicationRoleSelectorFamily(communicationType));
        return validateSchema(partyCommunicationRoleSchema, communicationRole);
    },
});
export const localPartyCommunicationChannelEmailAddressValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/emailAddress',
    get: ({ get }) => {
        const emailAddress = get(localPartyCommunicationChannelEmailAddressSelector);
        return validateSchema(partyEmailSchema, emailAddress);
    },
});
export const localPartyCommunicationChannelCountryCallingCodeValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/countryCallingCode',
    get: ({ get }) => {
        const countryCallingCode = get(localPartyCommunicationChannelCountryCallingCodeSelector);
        return validateSchema(partyCountryCallingCodeSchema, countryCallingCode);
    },
});
export const localPartyCommunicationChannelAreaCodeValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/areaCode',
    get: ({ get }) => {
        const areaCode = get(localPartyCommunicationChannelAreaCodeSelector);
        return validateSchema(partyAreaCodeSchema, areaCode);
    },
});
export const localPartyCommunicationChannelLocalPhoneNumberValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/localPhoneNumber',
    get: ({ get }) => {
        const localPhoneNumber = get(localPartyCommunicationChannelCommunicationPhoneNumberSelector);
        return validateSchema(partyLocalPhoneNumberSchema, localPhoneNumber);
    },
});
export const localPartyCommunicationChannelEmailFormValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/emailForm',
    get: ({ get }) => allValid({
        partyCommunicationRole: get(localPartyCommunicationChannelCommunicationRoleValidationSelectorFamily(CommunicationTypeEnum.EMAIL)),
        partyEmail: get(localPartyCommunicationChannelEmailAddressValidationSelector),
    }),
});
export const localPartyCommunicationChannelPhoneNumberFormValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/phoneNumberForm',
    get: ({ get }) => allValid({
        partyCommunicationRole: get(localPartyCommunicationChannelCommunicationRoleValidationSelectorFamily(CommunicationTypeEnum.TELEPHONE)),
        partyCountryCallingCode: get(localPartyCommunicationChannelCountryCallingCodeValidationSelector),
        partyAreaCode: get(localPartyCommunicationChannelAreaCodeValidationSelector),
        partyPhoneNo: get(localPartyCommunicationChannelLocalPhoneNumberValidationSelector),
    }),
});
export const localPartyCommunicationChannelCountryValidationSelectorFamily = selectorFamily({
    key: 'validation/local/partyCommunicationChannel/country',
    get: (communicationType) => ({ get }) => {
        const country = get(localPartyCommunicationChannelCountrySelectorFamily(communicationType));
        return validateSchema(partyCountrySchema, country);
    },
});
export const localPartyCommunicationChannelPostalCodeValidationSelectorFamily = selectorFamily({
    key: 'validation/local/partyCommunicationChannel/postalCode',
    get: (communicationType) => ({ get }) => {
        const countrySelector = localPartyCommunicationChannelCountrySelectorFamily(communicationType);
        const postalCodeSelector = localPartyCommunicationChannelPostalCodeSelectorFamily(communicationType);
        const country = get(countrySelector);
        const postalCode = get(postalCodeSelector);
        if (country !== null) {
            return validateSchema(partyForeignPostalCodeSchema, {
                country,
                postalCode,
            });
        }
        return validateSchema(partyPostalCodeSchema, postalCode);
    },
});
export const localPartyCommunicationChannelCityValidationSelectorFamily = selectorFamily({
    key: 'validation/local/partyCommunicationChannel/city',
    get: (communicationType) => ({ get }) => {
        const city = get(localPartyCommunicationChannelCitySelectorFamily(communicationType));
        return validateSchema(partyCitySchema, city);
    },
});
export const localPartyCommunicationChannelStreetValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/street',
    get: ({ get }) => {
        const street = get(localPartyCommunicationChannelStreetSelector);
        return validateSchema(partyStreetSchema, street);
    },
});
export const localPartyCommunicationChannelHouseNumberFromValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/houseNumberFrom',
    get: ({ get }) => {
        const houseNumberFrom = get(localPartyCommunicationChannelHouseNumberFromSelector);
        return validateSchema(partyHouseNumberFromSchema, houseNumberFrom);
    },
});
export const localPartyCommunicationChannelHouseNumberToValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/houseNumberTo',
    get: ({ get }) => {
        const houseNumberTo = get(localPartyCommunicationChannelHouseNumberToSelector);
        return validateSchema(partyHouseNumberToSchema, houseNumberTo);
    },
});
export const localPartyCommunicationChannelStaircaseValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/staircase',
    get: ({ get }) => {
        const staircase = get(localPartyCommunicationChannelStaircaseSelector);
        return validateSchema(partyStaircaseSchema, staircase);
    },
});
export const localPartyCommunicationChannelDoorNumberValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/doorNumber',
    get: ({ get }) => {
        const doorNumber = get(localPartyCommunicationChannelDoorNumberSelector);
        return validateSchema(partyDoorNumberSchema, doorNumber);
    },
});
export const localPartyCommunicationChannelAddressFormValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/addressForm',
    get: ({ get }) => allValid({
        partyCommunicationRole: get(localPartyCommunicationChannelCommunicationRoleValidationSelectorFamily(CommunicationTypeEnum.GEO)),
        partyCountry: get(localPartyCommunicationChannelCountryValidationSelectorFamily(CommunicationTypeEnum.GEO)),
        partyPostalCode: get(localPartyCommunicationChannelPostalCodeValidationSelectorFamily(CommunicationTypeEnum.GEO)),
        partyCity: get(localPartyCommunicationChannelCityValidationSelectorFamily(CommunicationTypeEnum.GEO)),
        partyStreet: get(localPartyCommunicationChannelStreetValidationSelector),
        partyHouseNumberFrom: get(localPartyCommunicationChannelHouseNumberFromValidationSelector),
        partyHouseNumberTo: get(localPartyCommunicationChannelHouseNumberToValidationSelector),
        partyStaircase: get(localPartyCommunicationChannelStaircaseValidationSelector),
        partyDoorNumber: get(localPartyCommunicationChannelDoorNumberValidationSelector),
    }),
});
export const localPartyCommunicationChannelPoboxNumberValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/poboxNumber',
    get: ({ get }) => {
        const poboxNumber = get(localPartyCommunicationChannelPoboxNumberSelector);
        return validateSchema(partyPoboxNumberSchema, poboxNumber);
    },
});
export const localPartyCommunicationChannelPostboxFormValidationSelector = selector({
    key: 'validation/local/partyCommunicationChannel/postboxForm',
    get: ({ get }) => allValid({
        partyCommunicationRole: get(localPartyCommunicationChannelCommunicationRoleValidationSelectorFamily(CommunicationTypeEnum.POSTBOX)),
        partyCountry: get(localPartyCommunicationChannelCountryValidationSelectorFamily(CommunicationTypeEnum.POSTBOX)),
        partyPostalCode: get(localPartyCommunicationChannelPostalCodeValidationSelectorFamily(CommunicationTypeEnum.POSTBOX)),
        partyCity: get(localPartyCommunicationChannelCityValidationSelectorFamily(CommunicationTypeEnum.POSTBOX)),
        partyPoboxNumber: get(localPartyCommunicationChannelPoboxNumberValidationSelector),
    }),
});
